import type { InputSwitchChangeEvent } from "primereact/inputswitch";
import { useEffect, useMemo, useState } from "react";

import { useStableRest } from "../../utils/use-stable-rest";
import {
  GANTT_INDICATORS_HINT,
  GANTT_INDICATORS_LAST_ACTIVE_MESSAGE,
  GANTT_INDICATORS_TITLE,
} from "./messages";
import type { GanttIndicatorsDialogProps } from "./types-gantt-indicators-dialog";

/** ViewModel do GanttIndicatorsDialog: rascunho local, trava do último ativo e Salvar/Cancelar; sem JSX. */
export function useGanttIndicatorsDialogViewModel({
  visible,
  onHide,
  options,
  value,
  onChange,
  onApply,
  title = GANTT_INDICATORS_TITLE,
  hint = GANTT_INDICATORS_HINT,
  warning = GANTT_INDICATORS_LAST_ACTIVE_MESSAGE,
  minActive = 1,
  testId,
  itemTestIdPrefix = "indicator",
  className,
  style,
  classNames,
  ...props
}: GanttIndicatorsDialogProps) {
  const rest = useStableRest(props);
  const [draft, setDraft] = useState<string[]>(value);

  // Cada abertura começa do que está em vigor; o que foi mexido e cancelado não volta.
  useEffect(() => {
    if (visible) setDraft(value);
    // `value` só importa na abertura: o rascunho não pode ser sobrescrito enquanto o usuário edita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  return useMemo(() => {
    const keys = options.map(({ key }) => key);
    const active = keys.filter((key) => draft.includes(key));
    const atMinimum = active.length <= minActive;

    return {
      rest,
      visible,
      dialogTestId: `${testId}-dialog`,
      footerTestId: `${testId}-footer`,
      className,
      style,
      classNames,
      title,
      testId,
      warningTestId: `${testId}-warning`,
      saveTestId: `${testId}-save`,
      /** No mínimo: o aviso troca a dica e os ativos travam. */
      atMinimum,
      message: atMinimum ? warning : hint,
      cancel: onHide,
      save: () => {
        onChange(active);
        onApply?.();
      },
      items: options.map((option) => {
        const checked = draft.includes(option.key);
        return {
          ...option,
          color: option.color ?? "var(--ds-color-primary-900)",
          checked,
          locked: checked && atMinimum,
          rowTestId: `${itemTestIdPrefix}-row-${option.key}`,
          toggleTestId: `${itemTestIdPrefix}-${option.key}`,
          onToggle: ({ value: next }: InputSwitchChangeEvent) =>
            setDraft((current) => {
              const withoutKey = current.filter((key) => key !== option.key);
              if (!next && keys.filter((key) => current.includes(key)).length <= minActive) {
                return current;
              }
              return next ? [...withoutKey, option.key] : withoutKey;
            }),
        };
      }),
    };
  }, [
    visible,
    options,
    draft,
    minActive,
    title,
    testId,
    warning,
    hint,
    onHide,
    onChange,
    onApply,
    itemTestIdPrefix,
    rest,
    className,
    style,
    classNames,
  ]);
}
